import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { PrivyProvider, usePrivy, useWallets } from '@privy-io/react-auth';

export const PRIVY_APP_ID = 'cmlyo0j7200f90cju4qsglht7';
export const RPC_ENDPOINT =
  'https://distinguished-polished-season.solana-mainnet.quiknode.pro/80c484293d2ebe3c37efc847acb4bff743b2481c/';

// ── Default (SSR-safe) wallet state ─────────────────────────────────────────
interface WalletState {
  wallet: string | null;
  connected: boolean;
  ready: boolean;
  balance: number | null;
  login: () => void;
  logout: () => void;
  solanaWallet: any | null;
}

const DEFAULT_STATE: WalletState = {
  wallet: null,
  connected: false,
  ready: false,
  balance: null,
  login: () => {},
  logout: () => {},
  solanaWallet: null,
};

const WalletCtx = createContext<WalletState>(DEFAULT_STATE);

// ── Fetch SOL balance via JSON-RPC ────────────────────────────────────────────
async function fetchSolBalance(address: string): Promise<number | null> {
  try {
    const res = await fetch(RPC_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method: 'getBalance',
        params: [address],
      }),
    });
    const data = await res.json();
    if (data?.result?.value !== undefined) {
      return data.result.value / 1e9; // lamports → SOL
    }
    return null;
  } catch {
    return null;
  }
}

// ── Inner bridge: reads Privy hooks → writes to context ──────────────────────
function PrivyWalletBridge({ children }: { children: React.ReactNode }) {
  const { authenticated, login, logout, ready, user } = usePrivy();
  const { wallets, ready: walletsReady } = useWallets();
  const [balance, setBalance] = useState<number | null>(null);

  // Get Solana wallet address - try multiple sources
  const { solanaAddress, solanaWalletObj } = useMemo(() => {
    // 1. Try useWallets() - works when wallet is actively connected
    //    In Privy v3: type is 'solana' | 'ethereum' for ConnectedWallet
    const all = wallets as any[];
    const fromWallets =
      all.find((w) => w.type === 'solana') ??
      all.find((w) => w.chainType === 'solana') ??
      (all.length > 0 ? all[0] : null);

    if (fromWallets?.address) {
      return { solanaAddress: fromWallets.address as string, solanaWalletObj: fromWallets };
    }

    // 2. Fallback: user.linkedAccounts (always available after auth)
    //    LinkedAccount wallets have chainType, not type
    if (user?.linkedAccounts) {
      const linked = (user.linkedAccounts as any[]).find(
        (a) => a.chainType === 'solana' && a.address
      );
      if (linked?.address) {
        return { solanaAddress: linked.address as string, solanaWalletObj: linked };
      }
    }

    return { solanaAddress: null, solanaWalletObj: null };
  }, [wallets, walletsReady, user]);

  // Fetch balance whenever wallet address changes
  useEffect(() => {
    if (!solanaAddress) {
      setBalance(null);
      return;
    }
    fetchSolBalance(solanaAddress).then(setBalance);
    // Refresh every 30 seconds
    const timer = setInterval(() => {
      fetchSolBalance(solanaAddress).then(setBalance);
    }, 30_000);
    return () => clearInterval(timer);
  }, [solanaAddress]);

  const value = useMemo<WalletState>(
    () => ({
      wallet: solanaAddress,
      // connected = authenticated + have an address (wallet OR linked account)
      connected: authenticated && !!solanaAddress,
      ready,
      balance,
      login,
      logout,
      solanaWallet: solanaWalletObj,
    }),
    [authenticated, ready, balance, login, logout, solanaAddress, solanaWalletObj]
  );

  return <WalletCtx.Provider value={value}>{children}</WalletCtx.Provider>;
}

// ── Provider: safe for SSR (no browser APIs at module level) ─────────────────
export function YDAOPrivyProvider({ children }: { children: React.ReactNode }) {
  const solanaConnectors = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { toSolanaWalletConnectors } = require('@privy-io/react-auth/solana');
      return toSolanaWalletConnectors();
    } catch {
      return undefined;
    }
  }, []);

  const config = useMemo(
    () => ({
      appearance: {
        theme: 'dark' as const,
        accentColor: '#9945FF',
        logo: 'https://y-dao.com/images/yd.jpg',
        walletChainType: 'solana-only' as const,
        walletList: [
          'detected_solana_wallets',
          'phantom',
          'solflare',
          'backpack',
        ] as any,
      },
      loginMethods: ['wallet' as const],
      ...(solanaConnectors
        ? { externalWallets: { solana: { connectors: solanaConnectors } } }
        : {}),
    }),
    [solanaConnectors]
  );

  return (
    <PrivyProvider appId={PRIVY_APP_ID} config={config as any}>
      <PrivyWalletBridge>{children}</PrivyWalletBridge>
    </PrivyProvider>
  );
}

// ── Hook: reads from context - SSR safe, no Privy hooks directly ─────────────
export function useYDAOWallet(): WalletState {
  return useContext(WalletCtx);
}
